import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, Building2, Search, Globe, MapPin, Building, Package, Phone, Mail, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

import { LOCATIE_TYPES , typeAangevinkt, typeToggle } from '@/lib/locatieTypes';

const Leveranciersbeheer = () => {
  const [leveranciers, setLeveranciers] = useState([]);
  const [categorien, setCategorien] = useState([]);
  const [locaties, setLocaties] = useState([]);
  const [producten, setProducten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLeverancier, setEditingLeverancier] = useState(null);
  const [filterCategorie, setFilterCategorie] = useState('alle');
  // 4.11: inactieve leveranciers zijn niet weg, ze staan achter dit filter
  const [filterStatus, setFilterStatus] = useState('actief');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [levResponse, catResponse, locResponse, prodResponse] = await Promise.all([
        axios.get(`${API}/leveranciers-centraal?status=${filterStatus}`),
        axios.get(`${API}/leverancier-categorien`),
        axios.get(`${API}/locaties`),
        axios.get(`${API}/voorraad-producten`)
      ]);
      setLeveranciers(levResponse.data);
      setCategorien(catResponse.data);
      setLocaties(locResponse.data);
      setProducten(prodResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Fout bij ophalen gegevens');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingLeverancier(null);
    setShowModal(true);
  };

  const handleEdit = (leverancier) => {
    setEditingLeverancier(leverancier);
    setShowModal(true);
  };

  const handleDelete = async (leverancierId, naam) => {
    if (!window.confirm(`Weet je zeker dat je "${naam}" wilt verwijderen?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/leveranciers-centraal/${leverancierId}`);
      toast.success('Leverancier verwijderd');
      fetchData();
    } catch (error) {
      console.error('Error deleting leverancier:', error);
      toast.error('Fout bij verwijderen');
    }
  };

  const handleSave = async (formData) => {
    try {
      if (editingLeverancier?._id) {
        await axios.put(`${API}/leveranciers-centraal/${editingLeverancier._id}`, formData);
        toast.success('Leverancier bijgewerkt en uitgerold naar locaties');
      } else {
        await axios.post(`${API}/leveranciers-centraal`, formData);
        toast.success('Leverancier toegevoegd en uitgerold naar locaties');
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error('Error saving leverancier:', error);
      toast.error('Fout bij opslaan');
    }
  };

  // Filter leveranciers
  const filteredLeveranciers = leveranciers.filter(lev => {
    const matchesCategorie = filterCategorie === 'alle' || lev.categorie === filterCategorie;
    const matchesSearch = !searchTerm || 
      lev.bedrijfsnaam?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lev.contactpersoon?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategorie && matchesSearch;
  });

  // Stats by category
  const stats = categorien.map(cat => ({
    naam: cat.naam,
    count: leveranciers.filter(l => l.categorie === cat.naam).length
  }));

  const getToepassingBadge = (leverancier) => {
    if (leverancier.toepassingType === 'alle') {
      return <Badge className="bg-emerald-100 text-emerald-700"><Globe className="w-3 h-3 mr-1" />Alle locaties</Badge>;
    } else if (leverancier.toepassingType === 'locaties') {
      const count = leverancier.specifiekLocaties?.length || 0;
      return <Badge className="bg-blue-100 text-blue-700"><MapPin className="w-3 h-3 mr-1" />{count} locatie(s)</Badge>;
    } else if (leverancier.toepassingType === 'locatietypes') {
      const types = leverancier.locatieTypes?.join(', ') || '';
      return <Badge className="bg-orange-100 text-orange-700"><Building className="w-3 h-3 mr-1" />Type: {types}</Badge>;
    }
    return <Badge variant="outline">Onbekend</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-slate-600">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Leveranciersbeheer</h1>
          <p className="text-slate-500">Beheer centrale leveranciers voor alle locaties</p>
        </div>
        <Button onClick={handleAdd} className="bg-tw-gold hover:bg-tw-gold/90 text-tw-blue">
          <Plus className="w-4 h-4 mr-2" />
          Nieuwe leverancier
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-slate-800">{leveranciers.length}</div>
              <div className="text-sm text-slate-500">Totaal</div>
            </div>
          </CardContent>
        </Card>
        {stats.slice(0, 5).map(stat => (
          <Card key={stat.naam}>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-slate-800">{stat.count}</div>
                <div className="text-sm text-slate-500">{stat.naam}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Zoeken op naam of contactpersoon..."
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-48">
              <Select value={filterCategorie} onValueChange={setFilterCategorie}>
                <SelectTrigger>
                  <SelectValue placeholder="Categorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="alle">Alle categorieën</SelectItem>
                  {categorien.map(cat => (
                    <SelectItem key={cat._id} value={cat.naam}>{cat.naam}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger data-testid="leverancier-status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="actief">Actief</SelectItem>
                  <SelectItem value="inactief">Inactief</SelectItem>
                  <SelectItem value="alle">Alle statussen</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Bedrijfsnaam</TableHead>
                <TableHead>Categorie</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Toepassing</TableHead>
                <TableHead>Producten</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLeveranciers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    {searchTerm || filterCategorie !== 'alle' 
                      ? 'Geen leveranciers gevonden met deze filters'
                      : 'Nog geen leveranciers. Klik op "Nieuwe leverancier" om te beginnen.'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredLeveranciers.map((leverancier) => (
                  <TableRow key={leverancier._id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-slate-400" />
                        <div>
                          <div className="font-medium">{leverancier.bedrijfsnaam}</div>
                          {leverancier.website && (
                            <a 
                              href={leverancier.website.startsWith('http') ? leverancier.website : `https://${leverancier.website}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                            >
                              <ExternalLink className="w-3 h-3" />
                              Website
                            </a>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{leverancier.categorie}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {leverancier.contactpersoon && (
                          <div className="font-medium">{leverancier.contactpersoon}</div>
                        )}
                        {leverancier.telefoon && (
                          <div className="flex items-center gap-1 text-slate-500">
                            <Phone className="w-3 h-3" />
                            {leverancier.telefoon}
                          </div>
                        )}
                        {leverancier.email && (
                          <div className="flex items-center gap-1 text-slate-500">
                            <Mail className="w-3 h-3" />
                            {leverancier.email}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{getToepassingBadge(leverancier)}</TableCell>
                    <TableCell>
                      {leverancier.producten?.length > 0 ? (
                        <Badge variant="secondary">
                          <Package className="w-3 h-3 mr-1" />
                          {leverancier.producten.length} product(en)
                        </Badge>
                      ) : (
                        <span className="text-slate-400 text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={leverancier.actief ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>
                        {leverancier.actief ? 'Actief' : 'Inactief'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(leverancier)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(leverancier._id, leverancier.bedrijfsnaam)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <LeverancierModal
        open={showModal}
        onClose={() => setShowModal(false)}
        leverancier={editingLeverancier}
        categorien={categorien}
        locaties={locaties}
        producten={producten}
        onSave={handleSave}
      />
    </div>
  );
};

// Leverancier Add/Edit Modal
const LeverancierModal = ({ open, onClose, leverancier, categorien, locaties, producten, onSave }) => {
  const [formData, setFormData] = useState({
    bedrijfsnaam: '',
    categorie: '',
    contactpersoon: '',
    telefoon: '',
    email: '',
    email_1: '',
    email_2: '',
    email_3: '',
    website: '',
    toepassingType: 'alle',
    specifiekLocaties: [],
    locatieTypes: [],
    heeftContract: false,
    contractDuur: '',
    contractKosten: '',
    producten: [],
    actief: true
  });
  const [productSearch, setProductSearch] = useState('');

  useEffect(() => {
    if (leverancier) {
      setFormData({
        bedrijfsnaam: leverancier.bedrijfsnaam || '',
        categorie: leverancier.categorie || '',
        contactpersoon: leverancier.contactpersoon || '',
        telefoon: leverancier.telefoon || '',
        email: leverancier.email || '',
        email_1: leverancier.email_1 || leverancier.email || '',
        email_2: leverancier.email_2 || '',
        email_3: leverancier.email_3 || '',
        website: leverancier.website || '',
        toepassingType: leverancier.toepassingType || 'alle',
        specifiekLocaties: leverancier.specifiekLocaties || [],
        locatieTypes: leverancier.locatieTypes || [],
        heeftContract: leverancier.heeftContract || false,
        contractDuur: leverancier.contractDuur || '',
        contractKosten: leverancier.contractKosten || '',
        producten: leverancier.producten || [],
        actief: leverancier.actief !== undefined ? leverancier.actief : true
      });
    } else {
      setFormData({
        bedrijfsnaam: '',
        categorie: '',
        contactpersoon: '',
        telefoon: '',
        email: '',
        email_1: '',
        email_2: '',
        email_3: '',
        website: '',
        toepassingType: 'alle',
        specifiekLocaties: [],
        locatieTypes: [],
        heeftContract: false,
        contractDuur: '',
        contractKosten: '',
        producten: [],
        actief: true
      });
    }
    setProductSearch('');
  }, [leverancier, open]);

  const handleSubmit = () => {
    if (!formData.bedrijfsnaam || !formData.categorie) {
      toast.error('Vul alle verplichte velden in');
      return;
    }
    onSave(formData);
  };

  const toggleLocatie = (locatieId) => {
    setFormData(prev => ({
      ...prev,
      specifiekLocaties: prev.specifiekLocaties.includes(locatieId)
        ? prev.specifiekLocaties.filter(id => id !== locatieId)
        : [...prev.specifiekLocaties, locatieId]
    }));
  };

  // 6.21/E14b: de wasstraat-checkbox dekt beide opslagwaarden (truckwash + legacy).
  const toggleLocatieType = (type) => {
    setFormData(prev => ({
      ...prev,
      locatieTypes: typeToggle(prev.locatieTypes, type,
        !typeAangevinkt(prev.locatieTypes, type)),
    }));
  };

  const toggleProduct = (productId) => {
    setFormData(prev => ({
      ...prev,
      producten: prev.producten.includes(productId)
        ? prev.producten.filter(id => id !== productId)
        : [...prev.producten, productId]
    }));
  };

  // Filter products by search
  const filteredProducten = producten.filter(p => 
    !productSearch || p.naam.toLowerCase().includes(productSearch.toLowerCase())
  );

  // Get selected product objects
  const selectedProducten = producten.filter(p => formData.producten.includes(p._id));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{leverancier ? 'Leverancier bewerken' : 'Nieuwe leverancier'}</DialogTitle>
          <DialogDescription>
            {leverancier 
              ? 'Wijzig de gegevens van deze leverancier. Wijzigingen worden automatisch uitgerold naar de toegewezen locaties.'
              : 'Voeg een nieuwe centrale leverancier toe. Deze wordt automatisch uitgerold naar de geselecteerde locaties.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Section 1: Basisgegevens */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-700 border-b pb-2">Basisgegevens</h3>
            
            <div>
              <Label>Bedrijfsnaam *</Label>
              <Input
                value={formData.bedrijfsnaam}
                onChange={(e) => setFormData({ ...formData, bedrijfsnaam: e.target.value })}
                placeholder="Kenotek B.V."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Categorie *</Label>
                <Select
                  value={formData.categorie}
                  onValueChange={(v) => setFormData({ ...formData, categorie: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecteer categorie" />
                  </SelectTrigger>
                  <SelectContent>
                    {categorien.map(cat => (
                      <SelectItem key={cat._id} value={cat.naam}>{cat.naam}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Website</Label>
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="www.leverancier.nl"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contactpersoon</Label>
                <Input
                  value={formData.contactpersoon}
                  onChange={(e) => setFormData({ ...formData, contactpersoon: e.target.value })}
                  placeholder="Jan de Vries"
                />
              </div>
              <div>
                <Label>Telefoon</Label>
                <Input
                  value={formData.telefoon}
                  onChange={(e) => setFormData({ ...formData, telefoon: e.target.value })}
                  placeholder="06-12345678"
                />
              </div>
            </div>

            <div>
              <Label>E-mail (primair) *</Label>
              <Input
                type="email"
                value={formData.email_1 || formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email_1: e.target.value, email: e.target.value })}
                placeholder="info@leverancier.nl"
                data-testid="leverancier-email-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>E-mail 2 (CC)</Label>
                <Input
                  type="email"
                  value={formData.email_2 || ''}
                  onChange={(e) => setFormData({ ...formData, email_2: e.target.value })}
                  placeholder="inkoop@leverancier.nl"
                  data-testid="leverancier-email-2"
                />
              </div>
              <div>
                <Label>E-mail 3 (CC)</Label>
                <Input
                  type="email"
                  value={formData.email_3 || ''}
                  onChange={(e) => setFormData({ ...formData, email_3: e.target.value })}
                  placeholder="extra@leverancier.nl"
                  data-testid="leverancier-email-3"
                />
              </div>
            </div>
          </div>

          {/* Section 2: Toepassing */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-700 border-b pb-2">Toepassing</h3>
            <p className="text-sm text-slate-500">Voor welke locaties is deze leverancier beschikbaar?</p>

            <div className="space-y-3">
              <div 
                className={`border rounded-lg p-4 cursor-pointer transition-colors ${formData.toepassingType === 'alle' ? 'border-blue-500 bg-blue-50' : 'hover:bg-slate-50'}`}
                onClick={() => setFormData({ ...formData, toepassingType: 'alle' })}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full border-2 ${formData.toepassingType === 'alle' ? 'border-blue-500 bg-blue-500' : 'border-slate-300'}`}>
                    {formData.toepassingType === 'alle' && <div className="w-2 h-2 bg-white rounded-full m-0.5"></div>}
                  </div>
                  <div>
                    <div className="font-medium">Alle locaties</div>
                    <div className="text-sm text-slate-500">Leverancier beschikbaar voor alle {locaties.length} locaties</div>
                  </div>
                </div>
              </div>

              <div 
                className={`border rounded-lg p-4 cursor-pointer transition-colors ${formData.toepassingType === 'locaties' ? 'border-blue-500 bg-blue-50' : 'hover:bg-slate-50'}`}
                onClick={() => setFormData({ ...formData, toepassingType: 'locaties' })}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full border-2 ${formData.toepassingType === 'locaties' ? 'border-blue-500 bg-blue-500' : 'border-slate-300'}`}>
                    {formData.toepassingType === 'locaties' && <div className="w-2 h-2 bg-white rounded-full m-0.5"></div>}
                  </div>
                  <div>
                    <div className="font-medium">Specifieke locaties</div>
                    <div className="text-sm text-slate-500">Kies specifieke locaties</div>
                  </div>
                </div>
                
                {formData.toepassingType === 'locaties' && (
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                    {locaties.map(loc => (
                      <label key={loc._id} className="flex items-center gap-2 p-2 hover:bg-white rounded cursor-pointer">
                        <Checkbox
                          checked={formData.specifiekLocaties.includes(loc._id)}
                          onCheckedChange={() => toggleLocatie(loc._id)}
                        />
                        <span className="text-sm">{loc.naam}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div 
                className={`border rounded-lg p-4 cursor-pointer transition-colors ${formData.toepassingType === 'locatietypes' ? 'border-blue-500 bg-blue-50' : 'hover:bg-slate-50'}`}
                onClick={() => setFormData({ ...formData, toepassingType: 'locatietypes' })}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full border-2 ${formData.toepassingType === 'locatietypes' ? 'border-blue-500 bg-blue-500' : 'border-slate-300'}`}>
                    {formData.toepassingType === 'locatietypes' && <div className="w-2 h-2 bg-white rounded-full m-0.5"></div>}
                  </div>
                  <div>
                    <div className="font-medium">Per locatietype</div>
                    <div className="text-sm text-slate-500">Alle locaties van een bepaald type</div>
                  </div>
                </div>
                
                {formData.toepassingType === 'locatietypes' && (
                  <div className="mt-4 flex flex-wrap gap-3">
                    {LOCATIE_TYPES.map(type => (
                      <label key={type.value} className="flex items-center gap-2 p-2 hover:bg-white rounded cursor-pointer">
                        <Checkbox
                          checked={typeAangevinkt(formData.locatieTypes, type.value)}
                          onCheckedChange={() => toggleLocatieType(type.value)}
                        />
                        <span className="text-sm">{type.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Section 3: Contract */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-700 border-b pb-2">Contract (optioneel)</h3>
            
            <div className="flex items-center space-x-2">
              <Switch
                checked={formData.heeftContract}
                onCheckedChange={(v) => setFormData({ ...formData, heeftContract: v })}
              />
              <Label>Standaard contract</Label>
            </div>

            {formData.heeftContract && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Contractduur</Label>
                  <Input
                    value={formData.contractDuur}
                    onChange={(e) => setFormData({ ...formData, contractDuur: e.target.value })}
                    placeholder="12 maanden"
                  />
                </div>
                <div>
                  <Label>Kosten</Label>
                  <Input
                    value={formData.contractKosten}
                    onChange={(e) => setFormData({ ...formData, contractKosten: e.target.value })}
                    placeholder="€500/maand"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Section 4: Producten */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-700 border-b pb-2">Producten koppelen</h3>
            <p className="text-sm text-slate-500">Welke producten levert deze leverancier?</p>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="Zoek producten..."
                className="pl-10"
              />
            </div>

            {/* Selected products */}
            {selectedProducten.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm">Geselecteerde producten ({selectedProducten.length}):</Label>
                <div className="border rounded-lg divide-y max-h-32 overflow-y-auto">
                  {selectedProducten.map(product => (
                    <div key={product._id} className="flex items-center justify-between p-2 hover:bg-slate-50">
                      <div className="flex items-center gap-2">
                        <Checkbox checked={true} onCheckedChange={() => toggleProduct(product._id)} />
                        <span className="text-sm">{product.naam}</span>
                        <Badge variant="outline" className="text-xs">{product.categorie}</Badge>
                      </div>
                      <button 
                        onClick={() => toggleProduct(product._id)}
                        className="text-slate-400 hover:text-red-500"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Available products */}
            <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
              {filteredProducten.filter(p => !formData.producten.includes(p._id)).length === 0 ? (
                <div className="p-4 text-center text-slate-500 text-sm">
                  {productSearch ? 'Geen producten gevonden' : 'Alle producten zijn geselecteerd'}
                </div>
              ) : (
                filteredProducten
                  .filter(p => !formData.producten.includes(p._id))
                  .slice(0, 10)
                  .map(product => (
                    <div 
                      key={product._id} 
                      className="flex items-center gap-2 p-2 hover:bg-slate-50 cursor-pointer"
                      onClick={() => toggleProduct(product._id)}
                    >
                      <Checkbox checked={false} />
                      <span className="text-sm">{product.naam}</span>
                      <Badge variant="outline" className="text-xs">{product.categorie}</Badge>
                    </div>
                  ))
              )}
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center space-x-2 pt-4 border-t">
            <Switch
              checked={formData.actief}
              onCheckedChange={(v) => setFormData({ ...formData, actief: v })}
            />
            <Label>Leverancier is actief</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuleren
          </Button>
          <Button onClick={handleSubmit}>
            {leverancier ? 'Opslaan' : 'Toevoegen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Leveranciersbeheer;
